import { API, Auth, Storage } from "aws-amplify";
import { AppEntityConfig } from "../redux/AppEntities";
import { AccountingUtils } from "revlock-accounting";
import UserActions from "redux/actions/user.actions";
import queryString from "query-string";
import {
    showAlert,
    showError,
    getMicroStrategyGroupName
} from "../utils/WebUtils";
import { Utils } from "revlock-webutils";

const customAuthorizationEnabled =
    process.env.REACT_APP_CUSTOM_AUTHORIZATION &&
    process.env.REACT_APP_CUSTOM_AUTHORIZATION === "true";

Storage.configure({ level: "public" });

const orgId = (state) => {
    let org =
        state &&
        state.currentOrganization &&
        state.currentOrganization.currentOrganization;
    if (!org) {
        throw Error("No organization selector, error while initiating update.");
    }
    return org.id;
};

export const PRIVATE_API_NAME = "revlock_api";
export const PUBLIC_API_NAME = "revlock_public_api";

export const fetcher = (entityName) =>
    async function (state, args) {
        let clientId = orgId(state);
        let config = AppEntityConfig(entityName);
        let getAllPath = config.getAllPath;

        if (config.paging) {
            let { currentPage, maxPerPage } = args;

            // Start index of items to show on screen
            args.start = currentPage * maxPerPage;

            // End index of items to show on screen.
            args.end = args.start + maxPerPage - 1;
        }

        Object.keys(args).forEach((arg) => {
            if (args[arg] instanceof Date) {
                args[arg] = args[arg].toISOString();
            }
        });

        return invokeApi(GET, `/private/${clientId}/${getAllPath}`, {
            queryStringParameters: args
        });
    };

export const fetcherCurrent = (entityName) =>
    async function (state, entityId) {
        const clientId = orgId(state);
        const path = AppEntityConfig(entityName).getPath;

        return invokeApi(GET, `/private/${clientId}/${path}/${entityId}`);
    };

export const fetcherCurrentTask = (entityName) =>
    async function (state, args) {
        let clientId = orgId(state);
        let { entityId } = args;

        return invokeApi(
            GET,
            `/private/${clientId}/activeTasks/all/${entityId}`,
            {
                queryStringParameters: args
            }
        );
    };

export const assignCurrentTask = (entityName) =>
    async function (state, params) {
        let clientId = orgId(state);

        let { taskId } = params;

        return invokeApi(POST, `/private/${clientId}/task/${taskId}/claim`, {
            body: params
        });
    };

export const onEntityAction = (entityName) =>
    async function (state, action, params) {
        let clientId = orgId(state);

        let { url, method } = action;

        return invokeApi(
            method || POST,
            `/private/${clientId}/${url}`,
            method !== GET
                ? {
                      body: params
                  }
                : {
                      queryStringParameters: params
                  }
        );
    };

export const creator = (entityName) =>
    async function (state, updates) {
        let clientId = orgId(state);
        let updatePath = AppEntityConfig(entityName).updatePath;

        return invokeApi(POST, `/private/${clientId}/${updatePath}`, {
            body: updates
        });
    };

export const updater = (entityName) =>
    async function (state, updates) {
        let clientId = orgId(state);
        let updatePath = AppEntityConfig(entityName).updatePath;

        return invokeApi(PUT, `/private/${clientId}/${updatePath}`, {
            body: updates
        });
    };

export const deleteAll = (entityName) =>
    async function (state, ids) {
        let clientId = orgId(state);
        let updatePath = AppEntityConfig(entityName).updatePath;

        return invokeApi(DELETE, `/private/${clientId}/${updatePath}`, {
            body: ids
        });
    };

export async function initDemoOrg(state, org) {
    return invokeApi(GET, `/private/${org.id}/_demoReLoad`);
}

export async function initProdTest(state, org) {
    return invokeApi(GET, `/private/${org.id}/_demoReferenceLoad`);
}

export async function getDefaultOrgConfigurations() {
    return invokeApi(GET, `/private/defaultOrgConfigs`);
}

export async function getOrgConfigurations(clientId) {
    return invokeApi(GET, `/private/${clientId}/orgConfigs`);
}

export async function putOrgConfigurations(clientId, configs) {
    return invokeApi(PUT, `/private/${clientId}/orgConfigs`, {
        body: configs
    });
}

export async function downloadOrgConfig(clientId) {
    return invokeApi(GET, `/private/${clientId}/orgConfig/download`);
}

export async function uploadOrgConfig(clientId, s3filename) {
    return invokeApi(POST, `/private/${clientId}/orgConfig/upload`, {
        body: { s3filename }
    });
}

export async function putOrgConfigurationsAndUpdateCache(
    clientId,
    configs,
    cachedOrgConfigs
) {
    const results = await putOrgConfigurations(clientId, configs);

    if (cachedOrgConfigs && configs && configs.configs) {
        // Now update the cached/application orgConfigs to they are updated
        // so if the user navigates away and then back to this page the
        // updated value is displayed without having to hard reload the page.
        for (const config of configs.configs) {
            const index = cachedOrgConfigs.findIndex((e) => e.id === config.id);

            if (index !== -1) {
                cachedOrgConfigs[index] = config;
            }
        }
    }

    return results;
}

export async function deleteOrgConfigurations(clientId, orgConfigIds) {
    return invokeApi(DELETE, `/private/${clientId}/orgConfigs`, {
        body: orgConfigIds
    });
}

/**
 * Clear Backend Caches
 */
export async function clearRACache(orgId) {
    return invokeApi(POST, `/private/admin/${orgId}/clear-arrangements`);
}

/**
 * Impersonation
 */
export async function startImpersonation(impersonatedUser) {
    return invokeApi(PUT, `/private/admin/impersonate`, {
        body: { impersonatedUser }
    });
}

export async function endImpersonation() {
    return invokeApi(DELETE, `/private/admin/impersonate`);
}

/**
 * DB Size
 */

export async function getDBSize() {
    return invokeApi(GET, `/private/admin/redis/dbSize`);
}

/**
 * Demo environment details
 *
 * @returns {Promise<*|void|undefined>}
 */
export async function getDemoEnvironmentDetails() {
    const url = "/demoenvdetails";
    return invokeApi(GET, url, {}, PUBLIC_API_NAME);
}

/**
 * What mode is the system running..? This is either {mode: "r"} or {mode: "rw"}
 *
 * @returns {Promise<*|void|undefined>}
 */
export async function getMode(details) {
    const url = details ? `/mode?details=${details}` : `/mode`;
    return invokeApi(GET, url, {}, PUBLIC_API_NAME, true);
}

export async function getUserIdp(email, clientId, revrecPath) {
    const url = `/user-idp`;
    return invokeApi(
        POST,
        url,
        {
            body: {
                email: email,
                clientId: clientId,
                revrecPath: revrecPath
            }
        },
        PUBLIC_API_NAME,
        true
    );
}

/**
 * Gets actualUsername by username from user pool
 */
export async function getActualUsername(params = {}) {
    if (!params.username) {
        throw Error("Username is not provided in queryString");
    }
    const username = encodeURIComponent(params.username);
    const url = `/actualUsername?username=${username}`;
    return invokeApi(GET, url, {}, PUBLIC_API_NAME, true);
}

/**
 * Creates the new User to Cognito
 */
export async function createNewUser(params = {}) {
    if (!params.username) {
        throw Error("User is not provided in as parameter");
    }

    const url = `/create-new-user`;
    return invokeApi(
        POST,
        url,
        {
            body: {
                ...params
            }
        },
        PUBLIC_API_NAME,
        true
    );
}

/**
 * Registers the new User SignUp to keycloak broker
 */
export async function registerNewUserToKeycloak(params = {}) {
    if (!params.username || !params.family_name || !params.given_name) {
        throw Error("User is not provided in as parameter");
    }

    const url = `/kk-admin-register-new-user`;
    return invokeApi(
        POST,
        url,
        {
            body: {
                ...params
            }
        },
        PUBLIC_API_NAME,
        true
    );
}

/**
 * Confirms the new User SignUp to keycloak broker
 */
export async function confirmNewUserToKeycloak(params = {}) {
    if (!params.username) {
        throw Error("User is not provided in as parameter");
    }

    const url = `/kk-admin-confirm-new-user`;
    return invokeApi(
        PUT,
        url,
        {
            body: {
                ...params
            }
        },
        PUBLIC_API_NAME,
        true
    );
}

export async function getOrgProfile(clientId) {
    const url = `/org-profile?clientId=${clientId}`;
    return invokeApi(GET, url, {}, PUBLIC_API_NAME, true);
}

/**
 * Set the REVLOCK system mode that is used for upgrades.
 *
 * @param mode either "r" or "rw"
 * @returns {Promise<*|void|undefined>}
 */
export async function setMode(mode = "rw", username) {
    return invokeApi(PUT, `/private/admin/mode/${mode}`, {
        body: {
            username
        }
    });
}

/**
 * Grant or revoke super admin access for a user.
 *
 * @param {Object} params - The parameters for the request
 * @param {string} params.userEmail - The email address of the user
 * @param {number} params.superAdminLevel - The super admin level (0-5)
 * @returns {Promise<*|void|undefined>}
 */
export async function grantSuperAdminAccess({ userEmail, superAdminLevel }) {
    return invokeApi(PUT, `/private/admin/superAdminAccess`, {
        body: { userEmail, superAdminLevel }
    });
}

/**
 * Grant, modify, or revoke warehouse access for a user.
 *
 * @param {Object} params - The parameters for the request
 * @param {string} params.userEmail - The email address of the user
 * @param {string} params.accessType - The type of access (REVOKE_ACCESS, ADD_READ_ACCESS, ADD_LIMITED_WRITE_ACCESS, REVOKE_LIMITED_WRITE_ACCESS)
 * @returns {Promise<*|void|undefined>}
 */
export async function grantWarehouseAccess({
    userEmail,
    accessType,
    temporaryPassword
}) {
    return invokeApi(PUT, `/private/admin/warehouseAccess`, {
        body: { userEmail, accessType, temporaryPassword }
    });
}

export async function saveInDirectExpenseItems(expenseItems, orgId) {
    const salesOrderId = expenseItems[0].salesOrderId;
    return invokeApi(
        PUT,
        `/private/${orgId}/indirect-expenses/${salesOrderId}`,
        {
            body: expenseItems
        }
    );
}

export async function saveExpenseItems(expenseItems, orgId) {
    const salesOrderId = expenseItems[0].salesOrderId;
    return invokeApi(PUT, `/private/${orgId}/direct-expenses/${salesOrderId}`, {
        body: expenseItems
    });
}

/**
 * Auto Suggest API
 */
export async function suggestOptions(params, orgId) {
    const toReturn = await invokeApi(
        GET,
        `/private/${orgId}/partialKeySearch?${queryString.stringify(params)}`
    );
    return toReturn;
}

export async function productSuggestOptions(params, orgId) {
    const toReturn = await invokeApi(
        GET,
        `/private/${orgId}/product/partialKeySearch?${queryString.stringify(
            params
        )}`
    );
    return toReturn;
}

export async function adminSuggestOptions(params) {
    const toReturn = await invokeApi(
        GET,
        `/private/admin/suggest-options?${queryString.stringify(params)}`
    );
    return toReturn;
}

/**
 * Customers
 */
export async function getCustomer(
    customerId,
    orgId,
    reqFromSalesOrderPage = false
) {
    return invokeApi(GET, `/private/${orgId}/customer/${customerId}`, {
        queryStringParameters: { reqFromSalesOrderPage }
    });
}

export async function getCustomerContracts(
    clientId,
    customerId,
    reqFromInvoicePage = false
) {
    return invokeApi(GET, `/private/${clientId}/customerContractss`, {
        queryStringParameters: { customerId, reqFromInvoicePage }
    });
}

export async function getCustomers(orgId, searchParams) {
    return invokeApi(GET, `/private/${orgId}/customers`, {
        queryStringParameters: searchParams
    });
}

/**
 * Errors
 */

export async function getErrors(orgId, searchParams) {
    return invokeApi(GET, `/private/${orgId}/errors`, {
        queryStringParameters: searchParams
    });
}

/**
 * ErrorCode
 */

export async function getErrorCode(errorCode) {
    return invokeApi(GET, `/private/errorCode`, {
        queryStringParameters: {
            errorCode
        }
    });
}

/**
 * ErrorInputSource
 */

export async function getErrorInputSource(orgId, searchParams) {
    return invokeApi(GET, `/private/${orgId}/errorInputSource`, {
        queryStringParameters: searchParams
    });
}

/**
 * ErrorInputSourceColumns
 */

export async function getErrorInputSourceColumns(orgId, searchParams) {
    return invokeApi(GET, `/private/${orgId}/errorInputSourceColumns`, {
        queryStringParameters: searchParams
    });
}

/**
 * Products
 */
export async function getProducts(orgId, queryStringParameters) {
    return invokeApi(GET, `/private/${orgId}/products`, {
        queryStringParameters
    });
}

export async function getProductGroups(orgId, queryStringParameters) {
    return invokeApi(GET, `/private/${orgId}/productGroups`, {
        queryStringParameters
    });
}

export async function getChildProducts(org, productId) {
    return invokeApi(
        GET,
        `/private/${org.id}/products/${Utils.encodeId(productId)}`
    );
}

export async function updateProducts(org, products) {
    return invokeApi(PUT, `/private/${org.id}/product`, {
        body: products
    });
}

/** Helper for performing sales order and export related filter calls. */
async function performSalesOrdersFilteredAction(params, url, method = GET) {
    const salesOrderSearchParams = Object.assign({}, params);

    return invokeApi(method, url, {
        queryStringParameters: salesOrderSearchParams
    });
}

/**
 * Export
 */
export async function getExportOptions(params) {
    return invokeApi(GET, `/private/${params.orgId}/export-options`);
}

export async function getExportCount(params) {
    return await performSalesOrdersFilteredAction(
        params,
        `/private/${params.orgId}/export/${Utils.encode(
            params.exportType
        )}/count`
    );
}

export async function downloadAllProducts(clientId) {
    return invokeApi(GET, `/private/${clientId}/product/download`);
}

export async function exportData(params) {
    return await performSalesOrdersFilteredAction(
        params,
        `/private/${params.orgId}/export/${Utils.encode(params.exportType)}`,
        POST
    );
}

export async function getExportedFiles(states, params) {
    return invokeApi(GET, `/private/${params.orgId}/exports`, {
        queryStringParameters: params
    });
}

export async function exportClientData(params) {
    return invokeApi(POST, `/private/${params.orgId}/exports`, {
        queryStringParameters: params
    });
}

/**
 * Import
 */
export async function importCustomFieldsData(orgId, params) {
    return invokeApi(POST, `/private/admin/${orgId}/populate-customfields`, {
        body: {
            ...params
        }
    });
}

export async function invokeDataConversionTrueUp(orgId, params) {
    return invokeApi(POST, `/private/admin/${orgId}/data-conversion-trueup`, {
        body: params
    });
}

export async function getDataConversionTrueUpStatus(orgId, jobId) {
    return invokeApi(
        GET,
        `/private/admin/${orgId}/data-conversion-trueup/${jobId}/status`
    );
}

/**
 * JournalAccountMappings
 */
export async function getJournalAccountMappings(orgId, params = {}) {
    return invokeApi(GET, `/private/${orgId}/journalAccountMappingss`, {
        queryStringParameters: params
    });
}

export async function updateJournalAccountMappings(orgId, body, params = {}) {
    return invokeApi(PUT, `/private/${orgId}/journalAccountMappings`, {
        body,
        queryStringParameters: params
    });
}

export async function getIncorrectJournalAccountMappings(orgId) {
    return invokeApi(GET, `/private/${orgId}/incorrect-jam-mappings`);
}

export async function downloadIncorrectMappings(orgId) {
    return invokeApi(GET, `/private/${orgId}/incorrect-jam-mappings`, {
        queryStringParameters: {
            download: true
        }
    });
}

/**
 * Revenue Story
 */
export async function getAvailableRevenueStoryTypes(orgId) {
    return invokeApi(GET, `/private/${orgId}/revenuestorytypes`);
}

export async function getRevenueStory(orgId, params = {}) {
    return invokeApi(GET, `/private/${orgId}/revenuestory`, {
        queryStringParameters: params
    });
}

export async function exportRevenueStory(orgId, params = {}) {
    return invokeApi(POST, `/private/${orgId}/revenuestory/export`, {
        queryStringParameters: params
    });
}

/**
 * Reports (User's Favourite)
 */
export async function updateUserFavorites(orgId, username, reports = {}) {
    return invokeApi(PUT, `/private/${orgId}/user/favorites`, {
        body: {
            reports,
            username
        }
    });
}

export async function getUserFavorites(orgId, username) {
    return invokeApi(POST, `/private/${orgId}/user/favorites`, {
        body: {
            username
        }
    });
}

/**
 * Manual Adjustments
 */
export async function createAdjustment(orgId, params = {}) {
    return invokeApi(POST, `/private/${orgId}/adjustments`, {
        body: params
    });
}

export async function searchAdjustments(orgId, params = {}) {
    return invokeApi(GET, `/private/${orgId}/adjustments`, {
        queryStringParameters: params
    });
}

export async function getAdjustmentTypes(orgId) {
    return invokeApi(GET, `/private/${orgId}/adjustment-types`);
}

export async function deleteAdjustment(orgId, groupId) {
    return invokeApi(DELETE, `/private/${orgId}/adjustments/${groupId}`);
}

export async function updateAdjustment(orgId, params = {}, groupId) {
    return invokeApi(PUT, `/private/${orgId}/adjustments/${groupId}`, {
        body: params
    });
}

/**
 * SalesOrders
 */

export async function getSalesOrderUpdateReport(orgId, searchParams) {
    return invokeApi(GET, `/private/${orgId}/salesorders/report`, {
        queryStringParameters: searchParams
    });
}

export async function getOrderReportsUsers(clientId) {
    return invokeApi(GET, `/private/${clientId}/order/report/usernames`);
}

export async function getOrdersReportExportCount(clientId, queryStringParams) {
    return invokeApi(GET, `/private/${clientId}/order/report/export/count`, {
        queryStringParameters: queryStringParams
    });
}

export async function exportOrdersReportEvents(clientId, queryStringParams) {
    return invokeApi(POST, `/private/${clientId}/order/report/export`, {
        queryStringParameters: queryStringParams
    });
}

export async function getSalesOrders(orgId, params) {
    return await performSalesOrdersFilteredAction(
        params,
        `/private/${orgId}/salesOrders`
    );
}

export async function updateSalesOrder(state, clientId) {
    return invokeApi(PUT, `/private/${clientId}/salesOrder`);
}

export async function getSalesOrderById(clientId, salesOrderId) {
    return invokeApi(GET, `/private/${clientId}/salesOrder/${salesOrderId}`);
}

export async function getMerchant(merchantId) {
    return invokeApi(GET, `/merchants`, {
        queryStringParameters: {
            website: Utils.encodeId(merchantId, true)
        }
    });
}

export async function displayCopilot(merchantId) {
    return invokeApi(GET, `/private/merchant/${merchantId}/copilot/enabled`);
}

export async function resolveChargebeeRegion(clientId) {
    return invokeApi(POST, `/private/${clientId}/chargebee-region`, {
        body: { discover: true }
    });
}

export async function getApiKey(orgId) {
    return invokeApi(GET, `/private/${orgId}/hotglue/apiKey`, {});
}

export async function updateHotglueConfig(config, orgId) {
    return invokeApi(POST, `/private/${orgId}/hotglue/updateConfig`, {
        body: {
            config
        }
    });
}

export async function updateMerchant(params) {
    return invokeApi(PATCH, `/merchants`, {
        body: params
    });
}

export async function adminGetMerchant(merchantId) {
    return invokeApi(GET, `/private/admin/merchant?merchantId=${merchantId}`);
}

export async function adminAddMerchant(params) {
    return invokeApi(POST, `/private/admin/merchant`, {
        body: params
    });
}

export async function adminUpdateMerchant(params) {
    return invokeApi(PUT, `/private/admin/merchant`, {
        body: params
    });
}

/**
 * Organizations
 * {
 *  name : 'Name of the Tenant',
 *  domain: 'Merchant Name',
 *  linkBE: 'Linked / Associated Business Entity for SSO'
 * }
 */
export async function signUpClient({ name, domain, linkBE, license, trial }) {
    name = name || domain;
    //default to domain name for client if not available

    return invokeApi(POST, `/org`, {
        body: {
            name,
            domain,
            license,
            trial,
            linkBE
        }
    });
}

export async function companyExists({ website, tenant, query }) {
    return invokeApi(GET, `/tenants`, {
        queryStringParameters: { website, tenant, query }
    });
}

export async function signUpTenant(params) {
    return invokeApi(POST, `/tenants`, {
        body: params
    });
}

export async function createSystemTenants() {
    return invokeApi(POST, `/private/admin/system-tenants`);
}

export async function updateOrganizationAttribute(orgId, params) {
    return invokeApi(PATCH, `/private/${orgId}/org`, {
        body: params
    });
}

export async function updateOrganization(state, org, periodType) {
    return invokeApi(PUT, `/private/${org.id}/org`, {
        body: {
            org,
            periodType
        }
    });
}

export async function resyncWarehouse(clientId) {
    const path = `/private/${clientId}/org/warehouse/resync`;
    return invokeApi(POST, path, {});
}

export async function getStatus(clientId) {
    const path = `/private/${clientId}/status`;
    return invokeApi(GET, path, {});
}

export async function contactSales(clientId, type, name) {
    const path = `/private/${clientId}/org/license/inquiry`;

    return invokeApi(POST, path, {
        body: {
            type,
            name
        }
    });
}

export async function upgradeLicensing(clientId, type, name, trial) {
    const path = `/private/${clientId}/org/license/upgrade`;

    return invokeApi(POST, path, {
        body: {
            type,
            name,
            trial
        }
    });
}

export async function getOrgApiKey(clientId) {
    return invokeApi(GET, `/private/${clientId}/org/get-api-key`);
}

export async function generateOrgApiKey(clientId) {
    return invokeApi(GET, `/private/${clientId}/org/generate-api-key`);
}

export async function getRRApiAdvancedConfig(orgId) {
    return invokeApi(GET, `/private/${orgId}/hotglue/rr-public-api-config`);
}

export async function updateRRApiAdvancedConfig(orgId, body) {
    return invokeApi(POST, `/private/${orgId}/hotglue/rr-public-api-config`, {
        body
    });
}

export async function getCurrentApiLimits(orgId) {
    return invokeApi(GET, `/private/${orgId}/current-api-limits`);
}

export async function updateCustomApiLimits(orgId, customLimits) {
    return invokeApi(PATCH, `/private/${orgId}/custom-api-limits`, {
        body: customLimits
    });
}

export async function invokePublicApiJob(orgId) {
    return invokeApi(POST, `/private/${orgId}/invoke-public-api-job`, {});
}

export async function salesOrderExists(clientId, salesOrderId) {
    return invokeApi(
        GET,
        `/private/${clientId}/salesOrder/${Utils.encodeId(salesOrderId)}/exists`
    );
}

export async function salesOrdersExists(clientId) {
    return invokeApi(GET, `/private/${clientId}/salesOrder/exists`);
}

export async function revenueSummary(clientId) {
    return invokeApi(GET, `/private/${clientId}/revenueSummaryForDashboard`);
}

export async function endingDeferredRevenueByCustomer(clientId) {
    return invokeApi(
        GET,
        `/private/${clientId}/endingDeferredRevenueByCustomer`
    );
}

export async function upgradePc1toPc2(siteName) {
    const url = `/partners/upgrade/productCatalog?siteName=${siteName}`;
    return invokeApi(PUT, url, {
        body: {
            siteName
        }
    });
}

export async function clearActgCloseRedis(clientId) {
    const url = `/private/${clientId}/unlock-accounting-close`;
    return invokeApi(POST, url, {
        body: {
            clientId
        }
    });
}

export async function isReSyncRequired(clientId) {
    return salesOrdersExists(clientId);
}

export async function inviteUserForOrganization(params) {
    const email = encodeURIComponent(params.username);
    const path = `/private/${params.clientId}/org/${email}`;
    return invokeApi(POST, path, {
        body: params
    });
}

export async function removeUserFromOrganization(state, params) {
    const email = encodeURIComponent(params.username);
    const path = `/private/${params.clientId}/org/${email}`;
    return invokeApi(DELETE, path, {
        body: params
    });
}

export async function updateOrganizationUser(state, params) {
    const email = encodeURIComponent(params.username);
    const path = `/private/${params.clientId}/org/${email}`;

    return invokeApi(PUT, path, {
        body: params
    });
}

export async function getOrganizationUsers(state, orgId) {
    return invokeApi(GET, `/private/${orgId}/users`);
}

export async function getWHBackups() {
    return invokeApi(GET, `/private/admin/whBackups`);
}

export async function restoreWH(database, s3Url, fileId) {
    return invokeApi(POST, `/private/admin/whRestore`, {
        body: {
            database,
            s3Url,
            fileId
        }
    });
}

export async function resetMarkedEnvironmentsForCleanup() {
    return invokeApi(POST, "/private/admin/orgs/expired");
}

export async function getMarkedEnvironmentsForCleanup(searchParams) {
    return invokeApi(GET, "/private/admin/orgs/expired", {
        queryStringParameters: searchParams
    });
}

export async function cleanUpEnvironments(params) {
    return invokeApi(DELETE, "/private/admin/orgs", {
        body: params
    });
}

export async function getAllOrganizations(
    start = 0,
    limit = 10,
    search = "",
    lookupStats = false
) {
    return invokeApi(
        GET,
        `/private/admin/orgs?start=${start}&limit=${limit}&search=${search}&lookupStats=${lookupStats}`
    );
}

export async function upgradeOrganization(
    tenantIds,
    versionTo,
    allTenants,
    numTenantsToUpgrade,
    jobWeight,
    stages,
    licenses
) {
    return invokeApi(POST, `/private/admin/upgrade`, {
        body: {
            tenantIds,
            versionTo,
            allTenants,
            numTenantsToUpgrade,
            jobWeight,
            stages,
            licenses
        }
    });
}

export async function deleteOrganization(orgId, cbsso = false) {
    return invokeApi(DELETE, `/private/admin/${orgId}/org`, {
        body: {
            cbsso
        }
    });
}

export async function initiateRecon(runForIgnoredTenants) {
    return invokeApi(POST, `/private/admin/orgs/recon`, {
        body: { runForIgnoredTenants }
    });
}

export async function invokeTestChargebeeApi(siteId) {
    return invokeApi(POST, `/private/admin/test-chargebee-internal-key`, {
        body: { siteId }
    });
}

export async function spawnContinuousExportJob() {
    return invokeApi(POST, `/private/admin/continuousExportJob`);
}

export async function initiateProdStats(params) {
    return invokeApi(POST, `/private/admin/orgs/prodStats`, {
        body: params
    });
}

export async function invokeRegression({ scenario, branch, email }) {
    return invokeApi(POST, `/private/admin/run-regression`, {
        body: { scenario, email, branch }
    });
}

export async function recordJobs({
    clientId,
    scenario,
    firstPeriod,
    branch,
    recordAllJournals
}) {
    return invokeApi(POST, `/private/admin/recordJob`, {
        body: { clientId, scenario, firstPeriod, branch, recordAllJournals }
    });
}

export async function invokeHGRollbackJob({ s3filename, logOnly }) {
    return invokeApi(POST, `/private/admin/hgrollbackJob`, {
        body: { s3filename, logOnly }
    });
}

export async function invokeUpdateUnearnedSummaryJob({ s3filename }) {
    return invokeApi(POST, `/private/admin/updateUnearnedSummaryJob`, {
        body: { s3filename }
    });
}

export async function generateJournalEntries(clientId, startPeriod, endPeriod) {
    const url = `/private/${clientId}/journal-entries`;

    return invokeApi(POST, url, {
        body: {
            startPeriod,
            endPeriod
        }
    });
}

export async function setAccountingPeriod(clientId, period) {
    const url = `/private/${clientId}/org/period/accounting`;
    return invokeApi(PUT, url, {
        body: {
            period
        }
    });
}

export async function setReportingPeriod(clientId, startPeriodsAgo) {
    const url = `/private/${clientId}/org/period/reporting`;
    return invokeApi(PUT, url, {
        body: {
            startPeriodsAgo
        }
    });
}

export async function updateHomeCurrency(clientId, currency) {
    const url = `/private/${clientId}/organization/currency`;
    return await invokeApi(PUT, url, {
        body: {
            currency
        }
    });
}

export async function clearSSPRules(clientId) {
    return invokeApi(DELETE, `/private/${clientId}/standaloneSellingPrices`);
}

export async function getPriceAnalysis(clientId) {
    return invokeApi(GET, `/private/${clientId}/priceAnalysis`);
}

export async function getSSPAnalysis(clientId, product) {
    return invokeApi(GET, `/private/${clientId}/sspAnalysis/${product}`);
}

export async function runSSPAnalyzerJob(clientId) {
    return invokeApi(POST, `/private/${clientId}/job/sspAnalyzer`);
}

export async function updateSSPFields(clientId, config) {
    return invokeApi(PUT, `/private/${clientId}/orgConfig/sspFields`, {
        body: {
            config
        }
    });
}

export async function updateJournalMappingFields(clientId, config) {
    return invokeApi(
        PUT,
        `/private/${clientId}/orgConfig/journalMappingFields`,
        {
            body: {
                config
            }
        }
    );
}

export async function republishAllSalesOrders(clientId) {
    return invokeApi(POST, `/private/${clientId}/republish`, {
        body: {}
    });
}

export async function validChargebeeToken(uiSession) {
    return invokeApi(
        POST,
        `/private/user/refresh-chargebee-token`,
        {
            body: {
                uiSession: uiSession
            }
        },
        PRIVATE_API_NAME
    );
}

export async function getUser() {
    let url = `/private/user`;
    return await invokeApi(GET, url, null, PRIVATE_API_NAME);
}

export async function getAuthorizations() {
    let url = `/private/authorizations`;
    return await invokeApi(GET, url, null, PRIVATE_API_NAME);
}

export async function getImpersonatedUser(username) {
    let url = `/private/admin/user/${username}`;

    return await invokeApi(GET, url, null, PRIVATE_API_NAME);
}

export async function addSuperUserToAccount(clientId, body = {}) {
    const url = `/private/admin/${clientId}/user`;
    return await invokeApi(POST, url, { body: body }, PRIVATE_API_NAME);
}

export async function overrideAutoSync(clientId, salesOrderId, syncDisabled) {
    const url = `/private/${clientId}/salesOrder/${Utils.encodeId(
        salesOrderId
    )}/override-autosync`;
    return await invokeApi(
        POST,
        url,
        { body: { syncDisabled } },
        PRIVATE_API_NAME
    );
}

export async function getOveragePS(clientId, customerId, salesOrderId) {
    const url = `/private/${clientId}/service-delivery/${customerId}/overage`;
    return await invokeApi(
        GET,
        url,
        { queryStringParameters: { salesOrderId } },
        PRIVATE_API_NAME
    );
}

export async function rerunRollForward({
    clientId,
    s3filename,
    forceRF,
    action
}) {
    const url = `/private/admin/${clientId}/rollforwardTools/${action}`;
    return await invokeApi(
        POST,
        url,
        { body: { s3filename, forceRF } },
        PRIVATE_API_NAME
    );
}

export async function salesOrdersUtils({
    clientId,
    s3filename,
    allOrders,
    action
}) {
    const url = `/private/admin/${clientId}/salesOrder/${action}`;
    return await invokeApi(
        POST,
        url,
        { body: { s3filename, allOrders } },
        PRIVATE_API_NAME
    );
}

export async function invokeArchive(clientId, salesOrderIds) {
    const url = `/private/admin/${clientId}/archive`;
    return await invokeApi(
        POST,
        url,
        { body: { salesOrderIds } },
        PRIVATE_API_NAME
    );
}

export async function invokeMigrateMerchant(
    clientId,
    fromMerchantId,
    toMerchantId
) {
    const url = `/private/admin/${clientId}/migrate-tenant`;
    return await invokeApi(
        POST,
        url,
        { body: { fromMerchantId, toMerchantId } },
        PRIVATE_API_NAME
    );
}

export async function extendTrial(clientId, merchantId) {
    const url = `/private/extend-trial`;
    return await invokeApi(
        POST,
        url,
        { body: { clientId, merchantId } },
        PRIVATE_API_NAME
    );
}
export async function invokeEnableRevrecNac(
    clientId,
    chargebeeSite,
    enableRevrecNav
) {
    const url = `/private/admin/${clientId}/enable-revrec-nav`;
    return await invokeApi(
        POST,
        url,
        { body: { chargebeeSite, enableRevrecNav } },
        PRIVATE_API_NAME
    );
}

export async function invokeClone(
    clientId,
    salesOrderIds = [],
    defaultUserEmail = null,
    cloneType = "TEMPORARY",
    cloneReason = "SUPPORT_ISSUE",
    clonedTenantName = null,
    referenceTicketNumber = null,
    additionalNotes = null
) {
    const url = `/private/admin/${clientId}/clone`;
    return await invokeApi(
        POST,
        url,
        {
            body: {
                salesOrderIds,
                defaultUserEmail,
                cloneType,
                cloneReason,
                clonedTenantName,
                referenceTicketNumber,
                additionalNotes
            }
        },
        PRIVATE_API_NAME
    );
}

export async function checkCanClone(clientId) {
    const url = `/private/admin/${clientId}/canClone`;
    return await invokeApi(GET, url, {}, PRIVATE_API_NAME);
}

export async function invokeCloneConfig(clientId, destClientId) {
    const url = `/private/admin/${clientId}/cloneConfig`;
    return await invokeApi(
        POST,
        url,
        { body: { srcClientId: clientId, destClientId } },
        PRIVATE_API_NAME
    );
}

export async function invokePublishConfig(clientId) {
    const url = `/private/admin/${clientId}/publishConfig`;
    return await invokeApi(POST, url, {}, PRIVATE_API_NAME);
}

export async function invokeMissingRA(clientId, jobId) {
    const url = `/private/admin/${clientId}/missingRA`;
    return await invokeApi(
        POST,
        url,
        { body: { clientId, jobId } },
        PRIVATE_API_NAME
    );
}

export async function invokeRecon(clientId, params) {
    const url = `/private/admin/${clientId}/reconOrg`;
    return await invokeApi(POST, url, { body: params }, PRIVATE_API_NAME);
}

export async function clearRecon(clientId) {
    const url = `/private/admin/${clientId}/clearReconErrors`;
    return await invokeApi(DELETE, url, {}, PRIVATE_API_NAME);
}

export async function getClones(clientId) {
    const url = `/private/admin/${clientId}/clone`;
    return await invokeApi(GET, url, {}, PRIVATE_API_NAME);
}

export async function getMigrationStatus(clientId) {
    const url = `/private/admin/${clientId}/migrate`;
    return await invokeApi(GET, url, {}, PRIVATE_API_NAME);
}

export async function getGlobalExportableViews() {
    return await invokeApi(
        GET,
        `/private/admin/global-exportable-views`,
        {},
        PRIVATE_API_NAME
    );
}

export async function invokeMigration(clientId, database, deleteSrcData) {
    const url = `/private/admin/${clientId}/migrate`;
    return await invokeApi(
        POST,
        url,
        {
            queryStringParameters: { database, deleteSrcData }
        },
        PRIVATE_API_NAME
    );
}

export async function invokeMigrationCleanup(clientId) {
    return await invokeApi(
        POST,
        `/private/admin/${clientId}/migration-cleanup`
    );
}

export async function restoreSnapshot(clientId, snapshot, environment) {
    const url = `/private/admin/${clientId}/restore`;
    return await invokeApi(
        POST,
        url,
        {
            body: {
                snapshot,
                environment
            }
        },
        PRIVATE_API_NAME
    );
}

export async function getUserOrganizations(withUsers = false) {
    let url = `/private/orgs`;
    if (withUsers) url = url + "?users=true";
    return invokeApi(GET, url);
}

export async function getOrgImages(state, orgs) {
    if (orgs) {
        let promisesOrgImages = orgs.map((org) => {
            return Storage.get(org.id + ".image", { level: "public" });
        });

        let imagesResult = await Promise.all(promisesOrgImages);
        imagesResult = imagesResult.filter((element) => element != null);

        orgs = orgs.map((org, index) => {
            org.image = imagesResult[index];
            return org;
        });

        // console.log("Successfully fetched user organization " + JSON.stringify(results));
        return orgs;
    }
}

export async function updateOrganizationImage(state, params) {
    let result = undefined;
    const isTestOrg = AccountingUtils.isTestId(params.orgId);
    const secondaryOrgId = isTestOrg
        ? AccountingUtils.toProdId(params.orgId)
        : AccountingUtils.toTestId(params.orgId);
    const orgIds = [params.orgId, secondaryOrgId];
    let promiseImages = orgIds.map((org) =>
        Storage.put(org + ".image", params.imgFile, {
            level: "public",
            contentType: "image/png"
        })
    );
    let results = await Promise.all(promiseImages);
    results = results.filter((element) => element != null);
    return Promise.resolve(results);
}

export async function updateCustomFieldTranslation(
    clientId,
    clientName,
    mstrProjectName,
    mstrLanguageName,
    isBillingMapping,
    newCustomFieldsMapping,
    originalCustomFieldsMapping
) {
    const mstrGroupName = getMicroStrategyGroupName(clientId, clientName);

    return invokeApi(
        POST,
        `/private/${clientId}/microstrategy/updatecustomfield`,
        {
            body: {
                mstrProjectName,
                mstrLanguageName,
                mstrGroupName,
                isBillingMapping,
                newCustomFieldsMapping,
                originalCustomFieldsMapping
            }
        }
    );
}

/**
 * Sync Jobs
 */
export async function triggerSync(state, params) {
    return invokeApi(POST, `/private/${params.orgId}/triggerSync`, {
        body: { syncParams: { s3filename: params.filename } }
    });
}

export async function archiveSync(orgId, jobId) {
    return invokeApi(PATCH, `/private/${orgId}/sync/${jobId}/archive`);
}

export async function archiveSyncJobGroup(orgId, jobId) {
    return invokeApi(
        PATCH,
        `/private/${orgId}/sync/${jobId}/archive-job-group`
    );
}

export async function getJobJson(orgId, jobId) {
    return invokeApi(GET, `/private/${orgId}/sync/${jobId}/json`);
}

export async function getJobGroupJson(orgId, jobGroupId) {
    return invokeApi(GET, `/private/${orgId}/sync/${jobGroupId}/json-group`);
}

export async function getSyncsStatus(orgId, queryStringParams) {
    return invokeApi(GET, `/private/${orgId}/sync/history`, {
        queryStringParameters: queryStringParams
    });
}

export async function getActiveSyncJobs(orgId) {
    return invokeApi(GET, `/private/${orgId}/sync/recent`);
}

export async function getSyncStatus(state, params) {
    return invokeApi(GET, `/private/${params.orgId}/syncStatus`, {
        queryStringParameters: { jobId: params.jobId }
    });
}

export async function getTaskSummary(state, params) {
    return invokeApi(GET, `/private/${params.orgId}/tasksSummary`);
}

export async function getCompanyId({ website, query }) {
    return invokeApi(
        GET,
        `/company`,
        {
            queryStringParameters: { website, query }
        },
        PUBLIC_API_NAME
    );
}

export async function deleteACEnvironment(org, cbsso = false) {
    const body = {
        cbsso
    };
    addBusinessEntityParam(org, body);

    return invokeApi(DELETE, `/private/${org.id}/environment`, {
        body
    });
}

export async function deleteEnvironment(orgId, cbsso = false) {
    return invokeApi(DELETE, `/private/${orgId}/environment`, {
        body: {
            cbsso
        }
    });
}

export async function addTestTransactions(orgId, dataSet) {
    return invokeApi(POST, `/private/${orgId}/environment/demo`, {
        dataSet
    });
}

export async function getHotglueSecureToken(orgId) {
    return invokeApi(GET, `/private/${orgId}/hotglue/secureToken`);
}

export async function hotglueAccountingLink(orgId, body) {
    return invokeApi(POST, `/private/${orgId}/hotglue/accounting-linked`, {
        body
    });
}

export async function hotglueAccountingUnLink(orgId, body) {
    return invokeApi(DELETE, `/private/${orgId}/hotglue/accounting-unlinked`, {
        body
    });
}

export async function getForeignExchangeRates(orgId) {
    return invokeApi(GET, `/private/${orgId}/hotglue/foreignExchangeRates`);
}

export async function hotglueLinkFlow(orgId, body) {
    return invokeApi(POST, `/private/${orgId}/hotglue/link-flow`, {
        body
    });
}

export async function hotglueUnLinkFlow(orgId, body) {
    return invokeApi(DELETE, `/private/${orgId}/hotglue/unlink-flow`, {
        body
    });
}

export async function hasSuccessfulSyncJob(orgId, queryStringParams) {
    return invokeApi(GET, `/private/${orgId}/sync/has-successful-job`, {
        queryStringParameters: queryStringParams
    });
}

export async function getSiteAccessKey(partner, siteKey, siteAccessKey) {
    const url = `/partners/sites/exists?partner=${partner}&siteKey=${siteKey}&siteAccessKey=${siteAccessKey}`;
    return invokeApi(GET, url);
}

export async function getPricingSummary(orgId, range) {
    const baseUrl = `/private/${orgId}/pricingSummary`;
    const url = range ? `${baseUrl}?range=${range}` : baseUrl;
    return invokeApi(GET, url);
}

export async function restoreSSPVersion(clientId, versionId) {
    return invokeApi(
        PUT,
        `/private/${clientId}/standaloneSellingPrice/version/${versionId}`
    );
}

export async function getSSPByVersionId(clientId, versionId) {
    return invokeApi(
        GET,
        `/private/${clientId}/standaloneSellingPrice/version/${versionId}`
    );
}

export async function getSSPVersions(clientId) {
    return invokeApi(
        GET,
        `/private/${clientId}/standaloneSellingPrice/versions`
    );
}

export async function getVersions(clientId, salesOrderId) {
    return invokeApi(
        GET,
        `/private/${clientId}/salesOrder/${Utils.encodeId(
            salesOrderId
        )}/versions`
    );
}

export async function restoreVersion(clientId, snapshotId, syncDisabled) {
    return invokeApi(
        POST,
        `/private/${clientId}/salesOrder/version/${snapshotId}`,
        {
            body: {
                syncDisabled
            }
        }
    );
}

export async function getSyncLockStatus(clientId) {
    return invokeApi(GET, `/private/${clientId}/lock`);
}

export async function unlockSyncLock(clientId) {
    return invokeApi(DELETE, `/private/${clientId}/lock`);
}

export async function downloadJobErrors(clientId) {
    return invokeApi(GET, `/private/admin/${clientId}/joberrors`);
}

export async function downloadDisabledSalesOrders(clientId) {
    return invokeApi(GET, `/private/${clientId}/disabledsalesorders`);
}

export async function overrideSyncForOrders(clientId, s3filename, action) {
    return invokeApi(
        POST,
        `/private/${clientId}/salesorders/syncoverride/${action}`,
        {
            body: { s3filename }
        }
    );
}

export async function uploadIgnoreAndDeleteJobErrors(clientId, s3filename) {
    return invokeApi(POST, `/private/admin/${clientId}/joberrors`, {
        body: { s3filename }
    });
}

export async function downloadMissingSalesOrders(clientId) {
    return invokeApi(GET, `/private/admin/${clientId}/missingsalesorders`);
}

export async function getRevenueStartDate(clientId) {
    return invokeApi(GET, `/private/admin/${clientId}/revenuestartdate`);
}

export async function addAuditChooseDomain() {
    return invokeApi(POST, `/private/user/audit-choose-a-domain`, {});
}

export async function addAuditEvent(clientId, queryStringParams, options = {}) {
    const init = {
        queryStringParameters: queryStringParams
    };
    if (options && options.abortSignal) {
        init.abortSignal = options.abortSignal;
    }
    return invokeApi(POST, `/private/${clientId}/audit`, init);
}

export async function getAuditTrailUsers(clientId) {
    return invokeApi(GET, `/private/${clientId}/audit/usernames`);
}

export async function getAuditTrailEventTypes(clientId) {
    return invokeApi(GET, `/private/${clientId}/audit/types`);
}

export async function getAuditTrailEntityTypes(clientId) {
    return invokeApi(GET, `/private/${clientId}/audit/entities`);
}

export async function getAuditTrailEvents(clientId, queryStringParams) {
    return invokeApi(GET, `/private/${clientId}/audit/events`, {
        queryStringParameters: queryStringParams
    });
}

export async function getAuditTrailExportCount(clientId, queryStringParams) {
    return invokeApi(GET, `/private/${clientId}/audit/export/count`, {
        queryStringParameters: queryStringParams
    });
}

export async function exportAuditTrailEvents(clientId, queryStringParams) {
    return invokeApi(POST, `/private/${clientId}/audit/export`, {
        queryStringParameters: queryStringParams
    });
}

export async function getSecurityUsers(clientId) {
    return invokeApi(GET, `/private/${clientId}/audit/security-users`);
}

export async function getSecurityEvents(clientId, queryStringParams) {
    return invokeApi(GET, `/private/${clientId}/audit/security-events`, {
        queryStringParameters: queryStringParams
    });
}

export async function executeGovernor() {
    return invokeApi(POST, `/private/admin/executeGovernor`, {});
}

export async function getProcesses(queryStringParams) {
    return invokeApi(GET, `/private/admin/processes`, {
        queryStringParameters: queryStringParams
    });
}

export async function cancelProcesses(clientId, jobGroupId) {
    return invokeApi(
        DELETE,
        `/private/admin/${clientId}/processes/${jobGroupId}`
    );
}

export async function markJobPriority(clientId, jobGroupId, priority) {
    return invokeApi(
        PUT,
        `/private/admin/${clientId}/processes/${jobGroupId}/priority/${priority}`
    );
}

export async function runProcessNow(clientId, jobGroupId) {
    return invokeApi(
        PUT,
        `/private/admin/${clientId}/processes/${jobGroupId}/signal`
    );
}

export async function generateGlobalExport(queryStringParams) {
    return invokeApi(POST, `/private/admin/global-exports`, {
        queryStringParameters: queryStringParams
    });
}

/**
 * Accounting Central
 */
function getBusinessEntityParam(org) {
    if (org && org.be_id) {
        return `?be_id=${org.be_id}`;
    }

    return "";
}

function addBusinessEntityParam(org, params = {}) {
    if (org && org.be_id) {
        params.be_id = org.be_id;
    }

    return params;
}

export async function getACErrorCodesImpactedByCode(org, params) {
    return invokeApi(
        GET,
        `/private/${org.id}/ac/errors/count${getBusinessEntityParam(org)}`,
        {
            queryStringParameters: addBusinessEntityParam(org, params)
        }
    );
}

export async function clearACJobErrorsNew(org, requestBody) {
    return invokeApi(
        PUT,
        `/private/${org.id}/ac/errors/new${getBusinessEntityParam(org)}`,
        {
            body: requestBody
        }
    );
}

export async function clearACJobErrors(org, items) {
    return invokeApi(
        PUT,
        `/private/${org.id}/ac/errors${getBusinessEntityParam(org)}`,
        {
            body: items
        }
    );
}

export async function getACErrorCodes() {
    return invokeApi(GET, `/private/ac/errorcodes`);
}

export async function getBusinessEntities(clientId) {
    return invokeApi(GET, `/private/${clientId}/ac/business_entities`);
}

export async function setBusinessEntities(clientId, entities = []) {
    return invokeApi(PUT, `/private/${clientId}/ac/business_entities`, {
        body: entities
    });
}

export async function getACGridMetaData(org, parameters) {
    // Query string params can be:
    // table or query (one of these required),
    // sourceEntity, columnDefaults, ignoreColumns, showCustomFieldsByDefault.
    if (parameters) {
        for (const key of Object.keys(parameters)) {
            if (
                parameters[key] !== undefined &&
                (Array.isArray(parameters[key]) ||
                    parameters[key].constructor == {}.constructor)
            ) {
                parameters[key] = JSON.stringify(parameters[key]);
            }
        }
    }

    return invokeApi(GET, `/private/${org.id}/ac/meta-data`, {
        queryStringParameters: addBusinessEntityParam(org, parameters)
    });
}

/** AC Journal Mapping Fields */
export async function getACBusinessEvents(clientId) {
    return invokeApi(GET, `/private/${clientId}/ac/business-events`);
}

export async function getACAvailableJournalMappingFields(clientId) {
    return invokeApi(
        GET,
        `/private/${clientId}/ac/available-journal-mapping-fields`
    );
}

export async function getACAvailableJournalLevelOfDetailsColumns(clientId) {
    return invokeApi(
        GET,
        `/private/${clientId}/ac/available-journal-level-columns`
    );
}

export async function getACAvailableJournalFilterColumns(clientId) {
    return invokeApi(
        GET,
        `/private/${clientId}/ac/available-journal-filter-columns`
    );
}

export async function getACStagingTableColumns(clientId, sourceEntity) {
    return invokeApi(GET, `/private/${clientId}/ac/staging-table-columns`, {
        queryStringParameters: { sourceEntity }
    });
}

export async function setACNextAutoJournalScheduleTime(org) {
    return invokeApi(PUT, `/private/${org.id}/ac/journal-schedule-time`, {
        queryStringParameters: addBusinessEntityParam(org)
    });
}

export async function getACAvailableStandardSites(org) {
    return invokeApi(GET, `/private/${org.id}/ac/available-standard-sites`, {
        queryStringParameters: addBusinessEntityParam(org)
    });
}

export async function getACPeriods(org) {
    return invokeApi(GET, `/private/${org.id}/ac/periods`, {
        queryStringParameters: addBusinessEntityParam(org)
    });
}

export async function getACInfo(org) {
    return invokeApi(GET, `/private/${org.id}/ac/info`, {
        queryStringParameters: addBusinessEntityParam(org)
    });
}

export async function getACJournalPeriodInfo(org) {
    return invokeApi(GET, `/private/${org.id}/ac/journalperiodinfo`, {
        queryStringParameters: addBusinessEntityParam(org)
    });
}

/** AC Tax Mappings */
export async function getACTaxMappings(org, queryStringParameters) {
    return invokeApi(GET, `/private/${org.id}/ac/tax-mappings`, {
        queryStringParameters: addBusinessEntityParam(
            org,
            queryStringParameters
        )
    });
}

export async function addACTaxMapping(org, values) {
    return invokeApi(
        POST,
        `/private/${org.id}/ac/tax-mappings${getBusinessEntityParam(org)}`,
        {
            body: {
                ...values
            }
        }
    );
}

export async function updateACTaxMapping(org, id, updates) {
    return invokeApi(
        PUT,
        `/private/${org.id}/ac/tax-mappings${getBusinessEntityParam(org)}`,
        {
            body: {
                id,
                updates
            }
        }
    );
}

export async function deleteACTaxMapping(org, id) {
    return invokeApi(
        DELETE,
        `/private/${org.id}/ac/tax-mappings${getBusinessEntityParam(org)}`,
        {
            body: {
                id
            }
        }
    );
}

export async function addAllDefaultACTaxMappings(org) {
    return invokeApi(
        POST,
        `/private/${org.id}/ac/tax-mappings/default${getBusinessEntityParam(
            org
        )}`
    );
}

export async function deleteAllACTaxMappings(org) {
    return invokeApi(
        DELETE,
        `/private/${org.id}/ac/tax-mappings${getBusinessEntityParam(org)}`,
        {
            body: {
                deleteAll: true
            }
        }
    );
}

export async function preloadACTaxMappings(org, s3FileName) {
    return invokeApi(
        POST,
        `/private/${org.id}/ac/tax-mappings/pre-upload${getBusinessEntityParam(
            org
        )}`,
        {
            body: {
                s3FileName
            }
        }
    );
}

export async function loadACTaxMappings(org, requestBody) {
    return invokeApi(
        POST,
        `/private/${
            org.id
        }/ac/tax-mappings/final-upload${getBusinessEntityParam(org)}`,
        {
            body: requestBody
        }
    );
}

/** AC Tax Groups */
export async function getACTaxGroups(org, queryStringParameters) {
    return invokeApi(GET, `/private/${org.id}/ac/tax-groups`, {
        queryStringParameters: addBusinessEntityParam(
            org,
            queryStringParameters
        )
    });
}

export async function addACTaxGroup(org, values) {
    return invokeApi(
        POST,
        `/private/${org.id}/ac/tax-groups${getBusinessEntityParam(org)}`,
        {
            body: {
                ...values
            }
        }
    );
}

export async function updateACTaxGroup(org, taxGroupName, updates) {
    return invokeApi(
        PUT,
        `/private/${org.id}/ac/tax-groups${getBusinessEntityParam(org)}`,
        {
            body: {
                taxGroupName,
                updates
            }
        }
    );
}

export async function deleteACTaxGroup(org, taxGroupName) {
    return invokeApi(
        DELETE,
        `/private/${org.id}/ac/tax-groups${getBusinessEntityParam(org)}`,
        {
            body: {
                taxGroupName
            }
        }
    );
}

export async function addAllDefaultACTaxGroups(org) {
    return invokeApi(
        POST,
        `/private/${org.id}/ac/tax-groups/default${getBusinessEntityParam(org)}`
    );
}

export async function deleteAllACTaxGroups(org) {
    return invokeApi(
        DELETE,
        `/private/${org.id}/ac/tax-groups${getBusinessEntityParam(org)}`,
        {
            body: {
                deleteAll: true
            }
        }
    );
}

export async function getACTaxMappingsByTaxGroupName(org, taxGroupName) {
    return invokeApi(
        GET,
        `/private/${org.id}/ac/tax-mappings/by-tax-group-name`,
        {
            queryStringParameters: addBusinessEntityParam(org, {
                taxGroupName: taxGroupName
            })
        }
    );
}

/** AC Accounts */
export async function getACAccountingEvents(clientId) {
    return invokeApi(GET, `/private/${clientId}/ac/accounting-events`);
}

export async function getACAccounts(org, queryStringParameters) {
    return invokeApi(GET, `/private/${org.id}/ac/accounts`, {
        queryStringParameters: addBusinessEntityParam(
            org,
            queryStringParameters
        )
    });
}

export async function getACTaxAccounts(org, queryStringParameters) {
    return invokeApi(GET, `/private/${org.id}/ac/tax-accounts`, {
        queryStringParameters: addBusinessEntityParam(
            org,
            queryStringParameters
        )
    });
}

export async function addACAccount(org, values) {
    return invokeApi(
        POST,
        `/private/${org.id}/ac/accounts${getBusinessEntityParam(org)}`,
        {
            body: {
                ...values
            }
        }
    );
}

export async function updateACAccount(org, accountNumber, updates) {
    return invokeApi(
        PUT,
        `/private/${org.id}/ac/accounts${getBusinessEntityParam(org)}`,
        {
            body: {
                accountNumber,
                updates
            }
        }
    );
}

export async function deleteACAccount(org, accountNumber) {
    return invokeApi(
        DELETE,
        `/private/${org.id}/ac/accounts${getBusinessEntityParam(org)}`,
        {
            body: {
                accountNumber
            }
        }
    );
}

export async function deleteAllACAccounts(org) {
    return invokeApi(
        DELETE,
        `/private/${org.id}/ac/accounts${getBusinessEntityParam(org)}`,
        {
            body: {
                deleteAll: true
            }
        }
    );
}

export async function preloadACAccounts(org, s3FileName) {
    return invokeApi(
        POST,
        `/private/${org.id}/ac/accounts/pre-upload${getBusinessEntityParam(
            org
        )}`,
        {
            body: {
                s3FileName
            }
        }
    );
}

export async function loadACAccounts(org, requestBody) {
    return invokeApi(
        POST,
        `/private/${org.id}/ac/accounts/final-upload${getBusinessEntityParam(
            org
        )}`,
        {
            body: requestBody
        }
    );
}

export async function addAllDefaultACAccounts(org) {
    return invokeApi(
        POST,
        `/private/${org.id}/ac/accounts/default${getBusinessEntityParam(org)}`
    );
}

/** AC JAMs */
export async function getACAccountingEventTypes(clientId) {
    return invokeApi(GET, `/private/${clientId}/ac/accounting-event-types`);
}

export async function getACJournalAccountMappings(org, queryStringParameters) {
    return invokeApi(GET, `/private/${org.id}/ac/jams`, {
        queryStringParameters: addBusinessEntityParam(
            org,
            queryStringParameters
        )
    });
}

export async function addACJournalAccountMapping(
    org,
    accountingEventType,
    groupName,
    values
) {
    return invokeApi(
        POST,
        `/private/${org.id}/ac/jams${getBusinessEntityParam(org)}`,
        {
            body: {
                accountingEventType,
                groupName,
                values
            }
        }
    );
}

export async function updateACJournalAccountMapping(
    org,
    accountingEventType,
    groupName,
    keys,
    updates
) {
    return invokeApi(
        PUT,
        `/private/${org.id}/ac/jams${getBusinessEntityParam(org)}`,
        {
            body: {
                accountingEventType,
                groupName,
                keys,
                updates
            }
        }
    );
}

export async function deleteACJournalAccountMapping(
    org,
    accountingEventType,
    groupName,
    values
) {
    return invokeApi(
        DELETE,
        `/private/${org.id}/ac/jams${getBusinessEntityParam(org)}`,
        {
            body: {
                accountingEventType,
                groupName,
                values
            }
        }
    );
}

export async function addAllACJournalAccountMappings(
    org,
    accountingEventType,
    groupName
) {
    return invokeApi(
        POST,
        `/private/${org.id}/ac/jams${getBusinessEntityParam(org)}`,
        {
            body: {
                accountingEventType,
                groupName,
                addMissing: true
            }
        }
    );
}

export async function deleteAllACJournalAccountMappings(
    org,
    accountingEventType,
    groupName
) {
    return invokeApi(
        DELETE,
        `/private/${org.id}/ac/jams${getBusinessEntityParam(org)}`,
        {
            body: {
                accountingEventType,
                groupName,
                deleteAll: true
            }
        }
    );
}

export async function preloadACJournalAccountMappings(org, s3FileName) {
    return invokeApi(
        POST,
        `/private/${org.id}/ac/jams/pre-upload${getBusinessEntityParam(org)}`,
        {
            body: {
                s3FileName
            }
        }
    );
}

export async function loadACJournalAccountMappings(org, requestBody) {
    return invokeApi(
        POST,
        `/private/${org.id}/ac/jams/final-upload${getBusinessEntityParam(org)}`,
        {
            body: requestBody
        }
    );
}

export async function downloadACJournalAccountMappings(org, requestBody) {
    return invokeApi(
        POST,
        `/private/${org.id}/ac/jams/download${getBusinessEntityParam(org)}`,
        {
            body: requestBody
        }
    );
}

/** AC Customers */
export async function getACCustomers(org, queryStringParameters) {
    return invokeApi(GET, `/private/${org.id}/ac/customers`, {
        queryStringParameters: addBusinessEntityParam(
            org,
            queryStringParameters
        )
    });
}

export async function getACCustomer(org, customerId) {
    return invokeApi(GET, `/private/${org.id}/ac/customers/${customerId}`, {
        queryStringParameters: addBusinessEntityParam(org)
    });
}

export async function getACCustomerBalances(org, customerId, period) {
    return invokeApi(
        GET,
        `/private/${org.id}/ac/customers/${customerId}/balances`,
        {
            queryStringParameters: addBusinessEntityParam(org, {
                period
            })
        }
    );
}

export async function getACEvents(org, queryStringParameters) {
    return invokeApi(GET, `/private/${org.id}/ac/events`, {
        queryStringParameters: addBusinessEntityParam(
            org,
            queryStringParameters
        )
    });
}

export async function getACSubscriptions(org, queryStringParameters) {
    return invokeApi(GET, `/private/${org.id}/ac/subscriptions`, {
        queryStringParameters: addBusinessEntityParam(
            org,
            queryStringParameters
        )
    });
}

export async function getACPeriodBalances(org, queryStringParameters) {
    return invokeApi(GET, `/private/${org.id}/ac/periodbalances`, {
        queryStringParameters: addBusinessEntityParam(
            org,
            queryStringParameters
        )
    });
}

export async function buildACPeriodBalances(
    org,
    params = { fromPeriod: 200001, includeClosedPeriods: true }
) {
    return invokeApi(
        POST,
        `/private/admin/${org.id}/ac/periodbalances${getBusinessEntityParam(
            org
        )}`,
        {
            body: params
        }
    );
}

export async function publishDeferredRevenueEvents(
    org,
    params = { startPeriod: 200001, endPeriod: 200001 }
) {
    return invokeApi(
        POST,
        `/private/admin/${
            org.id
        }/ac/publish-deferred-events${getBusinessEntityParam(org)}`,
        {
            body: params
        }
    );
}

export async function refreshUICache(orgId) {
    return invokeApi(POST, `/private/admin/${orgId}/ac/refresh-ui-cache`);
}
export async function populateReportingTables(orgId) {
    return invokeApi(
        POST,
        `/private/admin/${orgId}/ac/populate-reporting-tables`
    );
}
export async function updateExchangeRates(orgId) {
    return invokeApi(POST, `/private/admin/${orgId}/ac/update-exchange-rates`);
}
export async function backfillMissingSubledgerPostings(orgId) {
    return invokeApi(
        POST,
        `/private/admin/${orgId}/ac/backfill-missing-subledger-postings`
    );
}

export async function getACBalances(org, period, types) {
    return invokeApi(GET, `/private/${org.id}/ac/balances`, {
        queryStringParameters: addBusinessEntityParam(org, {
            period,
            types: types ? JSON.stringify(types) : undefined
        })
    });
}

/** AC Close/Reopen APIs */
export async function getACClosedPeriod(org) {
    return invokeApi(GET, `/private/${org.id}/ac/closedperiod`, {
        queryStringParameters: addBusinessEntityParam(org)
    });
}

export async function getACClosingRecords(org) {
    return invokeApi(GET, `/private/${org.id}/ac/closingrecords`, {
        queryStringParameters: addBusinessEntityParam(org)
    });
}

export async function reopenClosingRecords(org, recordsToReopen) {
    return invokeApi(
        DELETE,
        `/private/${org.id}/ac/closingrecords${getBusinessEntityParam(org)}`,
        {
            body: recordsToReopen
        }
    );
}

export async function getAccountingEventTypesToClose(org) {
    return invokeApi(GET, `/private/${org.id}/ac/closedrecords`, {
        queryStringParameters: addBusinessEntityParam(org)
    });
}

export async function closeACAccountingPeriod(
    org,
    period,
    closingRecord,
    postEntries = false
) {
    return invokeApi(
        POST,
        `/private/${org.id}/ac/${period}/closeperiod${getBusinessEntityParam(
            org
        )}`,
        {
            body: { postEntries, closingRecord }
        }
    );
}

export async function areACJournalsOutdated(org, throughActgPeriod) {
    const queryStringParameters = addBusinessEntityParam(org);
    if (throughActgPeriod) {
        queryStringParameters.throughActgPeriod = throughActgPeriod;
    }
    return invokeApi(GET, `/private/${org.id}/ac/arejournalsoutdated`, {
        queryStringParameters
    });
}

export async function exportACJournals(org, requestBody) {
    return invokeApi(
        POST,
        `/private/${org.id}/ac/exportjournals${getBusinessEntityParam(org)}`,
        {
            body: requestBody
        }
    );
}

export async function deleteACJournals(org, requestBody) {
    return invokeApi(
        DELETE,
        `/private/${org.id}/ac/journals${getBusinessEntityParam(org)}`,
        {
            body: requestBody
        }
    );
}

export async function availableJournalsToDelete(org) {
    return invokeApi(
        GET,
        `/private/${org.id}/ac/journals/count${getBusinessEntityParam(org)}`
    );
}

export async function isACJournalPostingOutdated(org) {
    return invokeApi(GET, `/private/${org.id}/ac/isjournalpostingoutdated`, {
        queryStringParameters: addBusinessEntityParam(org)
    });
}

export async function generateACJournals(org, startPeriod, endPeriod) {
    return invokeApi(
        POST,
        `/private/${org.id}/ac/generatejournals${getBusinessEntityParam(org)}`,
        {
            body: {
                startPeriod,
                endPeriod
            }
        }
    );
}

export async function hotglueAccountingACLink(orgId, body) {
    return invokeApi(POST, `/private/${orgId}/ac/hotglue/accounting-linked`, {
        body
    });
}

export async function hotglueAccountingACUnLink(orgId, body) {
    return invokeApi(
        DELETE,
        `/private/${orgId}/ac/hotglue/accounting-unlinked`,
        {
            body
        }
    );
}

/** Smoke Test APIs */
export async function getSmokeTestStatus() {
    return invokeApi(GET, `/private/admin/smoke-status`);
}

/** Beta Recon APIs */
export async function getBetaReconConfiguration() {
    return invokeApi(GET, `/private/admin/beta-recon`);
}

export async function updateBetaReconConfiguration(configuration) {
    return invokeApi(PUT, `/private/admin/beta-recon`, {
        body: configuration
    });
}

export async function enableBetaRecon() {
    return invokeApi(PUT, `/private/admin/beta-recon/enable`);
}

export async function disableBetaRecon() {
    return invokeApi(PUT, `/private/admin/beta-recon/disable`);
}

export async function runBetaRecon() {
    return invokeApi(PUT, `/private/admin/beta-recon/run`);
}

export async function getNamedQueries(queryStringParameters) {
    return invokeApi(GET, `/private/admin/named-queries`, {
        queryStringParameters
    });
}

export async function updateNamedQuery(namedQuery, updatedNamedQuery) {
    return invokeApi(PUT, `/private/admin/named-queries`, {
        body: {
            namedQuery,
            updatedNamedQuery
        }
    });
}

export async function addNamedQuery(namedQuery) {
    return invokeApi(POST, `/private/admin/named-queries`, {
        body: namedQuery
    });
}

export async function deleteNamedQuery(namedQueries) {
    const deleteKeys = namedQueries.map((q) => {
        return {
            orgId: q.orgId,
            name: q.name
        };
    });
    return invokeApi(DELETE, `/private/admin/named-queries`, {
        body: deleteKeys
    });
}

// Returns the report tabs that needs to show up on UI
export async function getNamedQueryReportTabs(clientId) {
    return invokeApi(
        GET,
        `/private/${clientId}/named-query-reports/report-tabs`
    );
}

// Returns the report names that needs to show up under report tab.
export async function getNamedQueryReports(clientId, queryStringParameters) {
    return invokeApi(GET, `/private/${clientId}/named-query-reports`, {
        queryStringParameters
    });
}

export async function getReportMetaData(
    org,
    queryName,
    params = {},
    options = {}
) {
    const init = {
        queryStringParameters: addBusinessEntityParam(
            org,
            Object.assign(
                {
                    queryName
                },
                params
            )
        )
    };
    if (options && options.abortSignal) {
        init.abortSignal = options.abortSignal;
    }
    return invokeApi(
        GET,
        `/private/${org.id}/named-query-reports/meta-data`,
        init
    );
}

export async function getNamedQueryReportMetaData(clientId, queryName) {
    return invokeApi(
        GET,
        `/private/${clientId}/named-query-reports/meta-data`,
        {
            queryStringParameters: {
                queryName
            }
        }
    );
}

export async function saveNamedQueryMetaData(clientId, body) {
    return invokeApi(
        POST,
        `/private/${clientId}/named-query-reports/meta-data/save`,
        { body }
    );
}

// Returns the report data (Results upon click of the report name).
export async function getReportResults(
    org,
    queryStringParameters,
    options = {}
) {
    const init = {
        queryStringParameters: addBusinessEntityParam(
            org,
            queryStringParameters
        )
    };
    if (options && options.abortSignal) {
        init.abortSignal = options.abortSignal;
    }
    return invokeApi(
        GET,
        `/private/${org.id}/named-query-reports/results`,
        init
    );
}

export async function getNamedQueryReportResults(
    clientId,
    queryStringParameters
) {
    return invokeApi(GET, `/private/${clientId}/named-query-reports/results`, {
        queryStringParameters
    });
}

// Count for pagination on report
export async function getNamedQueryReportCount(
    clientId,
    queryStringParameters
) {
    return invokeApi(GET, `/private/${clientId}/named-query-reports/count`, {
        queryStringParameters
    });
}

// Export (download) the report
export async function exportNamedQueryReport(clientId, exportParameters) {
    return invokeApi(POST, `/private/${clientId}/named-query-reports/export`, {
        body: exportParameters
    });
}

// Returns the report names that needs to show up under report tab.
export async function getNamedQueryReportByName(clientId, namespace, name) {
    return await invokeApi(
        GET,
        `/private/${clientId}/named-query-reports?namespace=${namespace}&queryName=${name}`
    );
}

// Returns the report tabs that needs to show up on UI
export async function getACNamedQueryReportTabs(clientId) {
    return invokeApi(
        GET,
        `/private/${clientId}/named-query-reports/report-tabs?namespace=ac`
    );
}

// Returns the report names that needs to show up under report tab.
export async function getACNamedQueryReports(clientId, queryStringParameters) {
    return invokeApi(
        GET,
        `/private/${clientId}/named-query-reports?namespace=ac`,
        {
            queryStringParameters
        }
    );
}

// Returns the report data (Results upon click of the report name).
export async function getACNamedQueryReportResults(
    clientId,
    queryStringParameters
) {
    return invokeApi(
        GET,
        `/private/${clientId}/named-query-reports/results?namespace=ac`,
        {
            queryStringParameters
        }
    );
}

// Count for pagination on report
export async function getACNamedQueryReportCount(
    clientId,
    queryStringParameters
) {
    return invokeApi(
        GET,
        `/private/${clientId}/named-query-reports/count?namespace=ac`,
        {
            queryStringParameters
        }
    );
}

// Export (download) the report
export async function exportACNamedQueryReport(clientId, exportParameters) {
    return invokeApi(POST, `/private/${clientId}/named-query-reports/export`, {
        body: exportParameters
    });
}

// Product Service Admin Action APIs

export async function getProductServiceUser(body) {
    return invokeApi(GET, `/private/admin/product-service/product-user`, {
        queryStringParameters: body
    });
}

export async function addProductServiceUser(body) {
    return invokeApi(POST, `/private/admin/product-service/product-user`, {
        body: body
    });
}

export async function deleteProductServiceUser(body) {
    return invokeApi(DELETE, `/private/admin/product-service/product-user`, {
        queryStringParameters: body
    });
}

export async function addProductServiceBusinessEntity(body) {
    return invokeApi(POST, `/private/admin/product-service/business-entity`, {
        body: body
    });
}

export async function getProductServiceBusinessEntity(body) {
    return invokeApi(GET, `/private/admin/product-service/business-entity`, {
        queryStringParameters: body
    });
}

export async function deleteProductServiceBusinessEntity(body) {
    return invokeApi(DELETE, `/private/admin/product-service/business-entity`, {
        queryStringParameters: body
    });
}

export async function getProductServiceBEUser(body) {
    return invokeApi(
        GET,
        `/private/admin/product-service/business-entity-user`,
        {
            queryStringParameters: body
        }
    );
}

export async function addProductServiceBEUser(body) {
    return invokeApi(
        POST,
        `/private/admin/product-service/business-entity-user`,
        {
            body: body
        }
    );
}

export async function deleteProductServiceBEUser(body) {
    return invokeApi(
        DELETE,
        `/private/admin/product-service/business-entity-user`,
        {
            queryStringParameters: body
        }
    );
}

export async function getProductServiceSourceBEMapping(body) {
    return invokeApi(
        GET,
        `/private/admin/product-service/business-entity-mapping/source`,
        {
            queryStringParameters: body
        }
    );
}

export async function getProductServiceDestinationBEMapping(body) {
    return invokeApi(
        GET,
        `/private/admin/product-service/business-entity-mapping/destination`,
        {
            queryStringParameters: body
        }
    );
}

export async function addProductServiceDestinationBEMapping(body) {
    return invokeApi(
        POST,
        `/private/admin/product-service/business-entity-mapping`,
        {
            body: body
        }
    );
}

export async function deleteProductServiceDestinationBEMapping(body) {
    return invokeApi(
        DELETE,
        `/private/admin/product-service/business-entity-mapping`,
        {
            queryStringParameters: body
        }
    );
}

export async function triggerPsdRADataFix(body) {
    return invokeApi(POST, `/private/admin/psd-ra-data-fix`, { body });
}

export async function migrateToProductService(body) {
    return invokeApi(
        POST,
        `/private/admin/product-service/migrate-to-product-service`,
        {
            body: body
        }
    );
}

export async function migrateCommonUserToProductService({
    s3filename,
    tenantLevel
}) {
    return invokeApi(
        POST,
        `/private/admin/product-service/migrate-common-user`,
        {
            body: { s3filename, tenantLevel: tenantLevel || false }
        }
    );
}

export async function rollbackCommonUserFromProductService({
    s3filename,
    tenantLevel
}) {
    return invokeApi(
        POST,
        `/private/admin/product-service/migrate-common-user`,
        {
            body: {
                s3filename,
                rollback: true,
                tenantLevel: tenantLevel || false
            }
        }
    );
}

export async function changeCustomerId({ orgId, s3filename }) {
    return invokeApi(POST, `/private/admin/change-customer-id`, {
        body: { orgId, s3filename }
    });
}

export async function migrateRevrecUserToProductService(clientId) {
    return invokeApi(
        POST,
        `/private/admin/product-service/migrate-revrec-user`,
        { body: { clientId } }
    );
}

export async function removeMigrationToProductService(body) {
    return invokeApi(
        DELETE,
        `/private/admin/product-service/remove-from-product-service`,
        {
            queryStringParameters: body
        }
    );
}

export async function isProductServiceDisabled(clientId) {
    return invokeApi(
        GET,
        `/private/admin/product-service/${clientId}/disable-product-service`
    );
}

export async function enableProductService(clientId) {
    return invokeApi(
        DELETE,
        `/private/admin/product-service/${clientId}/disable-product-service`
    );
}

export async function disableProductService(clientId) {
    return invokeApi(
        POST,
        `/private/admin/product-service/${clientId}/disable-product-service`
    );
}

/* Alerts and Issue Service */
export async function getAllIssuesWithJobs() {
    return invokeApi(GET, `/private/admin/alert-service/issues`);
}

export async function getOrgIssueSummaryData() {
    return invokeApi(GET, `/private/admin/alert-service/org-issue-summary`);
}

export async function updateIssue({ issueId, updateData }) {
    return invokeApi(PATCH, `/private/admin/alert-service/issue/${issueId}`, {
        body: updateData
    });
}

export async function getAllJobsForJobGroup({ jobGroupId }) {
    return invokeApi(GET, `/private/admin/alert-service/jobs/${jobGroupId}`);
}

export async function createIssues() {
    return invokeApi(POST, `/private/admin/alert-service/job-failure-issues`);
}

export async function getLastTimeIssueCreated() {
    return invokeApi(GET, `/private/admin/alert-service/last-issue-runtime`);
}

export async function getJobSummaryofTenants({ startTime, endTime }) {
    const params =
        startTime || endTime
            ? `?startTime=${startTime}&endTime=${endTime}`
            : "";
    return invokeApi(GET, `/private/admin/alert-service/job-summary` + params);
}

export async function recoveryActionForJob({
    jobId,
    jobGroupId,
    issueId,
    recoveryType
}) {
    return invokeApi(
        POST,
        `/private/admin/alert-service/recovery-issue-action`,
        {
            body: {
                jobId,
                jobGroupId,
                issueId,
                recoveryType
            }
        }
    );
}

export async function sendCMSAlerts() {
    return invokeApi(POST, `/private/admin/send-csm-alerts`);
}

export async function getJournalDiagnosticsS3Key(clientId) {
    return invokeApi(GET, `/private/${clientId}/journals/diagnostics-key`);
}

export async function runJournalDiagnostics(clientId, params) {
    return invokeApi(POST, `/private/${clientId}/journals/diagnostics`, {
        body: params
    });
}

// Export Settings API methods
export async function getACExportSettings(clientId, businessEntityId = null) {
    const queryParams = businessEntityId ? { be_id: businessEntityId } : {};
    return invokeApi(GET, `/private/${clientId}/ac/export-settings`, {
        queryStringParameters: queryParams
    });
}

export async function saveACExportSettings(
    clientId,
    settings,
    businessEntityId = null
) {
    const queryParams = businessEntityId ? { be_id: businessEntityId } : {};
    return invokeApi(PUT, `/private/${clientId}/ac/export-settings`, {
        queryStringParameters: queryParams,
        body: settings
    });
}

export async function getACExportOptions(clientId, businessEntityId = null) {
    const queryParams = businessEntityId ? { be_id: businessEntityId } : {};
    return invokeApi(GET, `/private/${clientId}/ac/export-options`, {
        queryStringParameters: queryParams
    });
}

export async function getCurrentContinuousExportTime(clientId) {
    return invokeApi(
        GET,
        `/private/${clientId}/ac/current-continuous-export-time`
    );
}

export async function resetContinuousExportTime(clientId, resetTime) {
    return invokeApi(
        POST,
        `/private/${clientId}/ac/reset-continuous-export-time`,
        {
            body: { resetTime }
        }
    );
}

export async function getExportHGLinked(clientId) {
    return invokeApi(GET, `/private/${clientId}/ac/export-hg-linked`);
}

// Tenant Migration API Functions
export async function exportTenantMigration(tenantId) {
    return invokeApi(POST, `/private/admin/tenant-migration/export`, {
        body: {
            tenantId
        }
    });
}

export async function importTenantMigration(tenantId, s3Path) {
    return invokeApi(POST, `/private/admin/tenant-migration/import`, {
        body: {
            tenantId,
            s3Path
        }
    });
}

export async function bulkInviteUsers(tenantId) {
    return invokeApi(POST, `/private/admin/${tenantId}/bulk-invite-users`, {
        body: {}
    });
}

// IMPORTANT: these wrappers MUST NOT be `async`. Amplify's REST cancel
// token WeakMap is keyed by the exact Promise object returned from
// `API.get/post/...`. Wrapping with `async () => API.get(...)` would
// produce a NEW outer Promise that is NOT in the WeakMap, which makes
// `API.cancel(outerPromise)` silently no-op and breaks request
// cancellation entirely. Returning the underlying Amplify promise
// directly preserves cancellability and is behaviourally equivalent for
// `await` callers (Amplify already converts sync throws to rejected
// promises internally).
export const GET = (apiName, path, init) => API.get(apiName, path, init);
export const POST = (apiName, path, init) => API.post(apiName, path, init);
export const PUT = (apiName, path, init) => API.put(apiName, path, init);
export const PATCH = (apiName, path, init) => API.patch(apiName, path, init);
export const DELETE = (apiName, path, init) => API.del(apiName, path, init);
export const HEAD = (apiName, path, init) => API.head(apiName, path, init);

export function setCookie(cName, cValue, path, minutes) {
    let expires = "";
    if (minutes) {
        let d = new Date();
        d.setTime(d.getTime() + minutes * 60 * 1000);
        expires = "; expires=" + d.toUTCString();
    }
    document.cookie = cName + "=" + cValue + expires + ";path=" + path;
}

export function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
        return parts.pop().split(";").shift();
    }
}

/**
 * Marker error thrown by `invokeApi` when an in-flight request is aborted via
 * the caller-supplied `abortSignal`. Callers (e.g. AC report components that
 * switch reports while a previous fetch is in flight) can branch on
 * `isCancelledRequestError(err)` to silently discard stale responses without
 * surfacing them as user-visible errors.
 */
export class CancelledRequestError extends Error {
    constructor(message = "Request cancelled") {
        super(message);
        this.name = "CancelledRequestError";
        this.isCancelledRequest = true;
    }
}

/**
 * Detects cancellation errors originating from either our own
 * `CancelledRequestError` or AWS Amplify's underlying axios cancel token.
 * Use this in catch-blocks that follow a cancellable `invokeApi` call so a
 * cancelled request is never rendered as a UI error for the newly-active
 * report after the user switches between AC reports quickly.
 */
export function isCancelledRequestError(error) {
    if (!error) return false;
    if (error instanceof CancelledRequestError) return true;
    if (error.isCancelledRequest === true) return true;
    if (error.name === "CancelledRequestError") return true;
    try {
        return typeof API.isCancel === "function" && API.isCancel(error);
    } catch (_) {
        return false;
    }
}

export async function invokeApi(
    method,
    path,
    myInit,
    apiName = PRIVATE_API_NAME,
    processError = true
) {
    let simulatedProfile;

    // Pull off the optional AbortSignal before forwarding `myInit` to Amplify
    // so we don't leak unknown options into the underlying axios call.
    const abortSignal = myInit && myInit.abortSignal;
    if (myInit && "abortSignal" in myInit) {
        delete myInit.abortSignal;
    }

    if (abortSignal && abortSignal.aborted) {
        throw new CancelledRequestError(
            `Request to ${path} was already aborted before dispatch`
        );
    }

    let inflightRequest;
    let abortListener;

    try {
        myInit = myInit || {};
        myInit.response = true;

        myInit.headers = myInit.headers || {};

        //if you are localhost track which profile you are
        //being simulated to be on using a cookie and also send this
        //to the server so it also knows you have switched

        if (window.location.hostname === "localhost") {
            simulatedProfile = getCookie("simulatedLocalRedirectProfile");
            if (simulatedProfile) {
                myInit.headers["x-profile"] = simulatedProfile;
            }
        }

        const isPublicApi = apiName === PUBLIC_API_NAME;
        if (!isPublicApi && customAuthorizationEnabled) {
            try {
                let session = await Auth.currentSession();
                myInit.headers["Authorization"] = session
                    .getIdToken()
                    .getJwtToken();
            } catch (e) {
                console.error(
                    "Unable to obtain a valid session and JWT token!!",
                    e
                );
                throw Error("User is not authenticated");
            }
        }

        // After the awaited Auth roundtrip the caller may have already
        // aborted (e.g. user switched AC reports while we were fetching a
        // session). Bail out before issuing the actual API call.
        if (abortSignal && abortSignal.aborted) {
            throw new CancelledRequestError(
                `Request to ${path} was aborted before dispatch`
            );
        }

        // Capture the in-flight Amplify promise BEFORE awaiting so we can
        // hand it to API.cancel() if the abort signal fires mid-flight.
        // The wrappers (GET/POST/...) MUST be non-async so the promise we
        // store here is the same Promise object Amplify registered in its
        // cancel-token WeakMap. See the comment block on those wrappers.
        inflightRequest = method(apiName, path, myInit);

        if (abortSignal) {
            // Dev-time sanity check: if the underlying request isn't
            // registered as cancellable, cancellation is broken — surface
            // it loudly instead of silently leaking stale responses.
            if (
                process.env.NODE_ENV !== "production" &&
                typeof API.hasCancelToken === "function" &&
                inflightRequest &&
                !API.hasCancelToken(inflightRequest)
            ) {
                console.warn(
                    `[invokeApi] abortSignal supplied for ${path} but the ` +
                        `returned promise is not cancellable. Check that the ` +
                        `method wrappers in rest/API.js are NOT async.`
                );
            }
            abortListener = () => {
                try {
                    if (typeof API.cancel === "function" && inflightRequest) {
                        API.cancel(
                            inflightRequest,
                            `Request to ${path} cancelled by caller`
                        );
                    }
                } catch (_) {
                    // Best-effort cancel; swallow so abort path still completes.
                }
            };
            abortSignal.addEventListener("abort", abortListener);
        }

        let response = await inflightRequest;

        // So this is a workaround that will download the response from S3 instead of response from API.
        // This is done to work around limitation in Lambda that limits the response to 6MB.
        if (response && response.data && response.data.s3Response) {
            const result = await Storage.get(response.data.s3File, {
                contentType: "text/plain",
                download: true
            });
            const responseStr = await result.Body.text();
            response.data = JSON.parse(responseStr);
        }

        return response.data;
    } catch (error) {
        // If the caller's AbortSignal fired (or Amplify reports the error as a
        // cancel), surface a CancelledRequestError without invoking any of the
        // user-facing error handling below. This is what allows AC report
        // components to switch between reports without ever leaking a stale
        // "request failed" alert into the newly-active report.
        if (
            (abortSignal && abortSignal.aborted) ||
            (typeof API.isCancel === "function" && API.isCancel(error))
        ) {
            throw new CancelledRequestError(`Request to ${path} was cancelled`);
        }

        if (processError) {
            const code = error && error.response && error.response.status;

            if (
                code === 401 ||
                ((typeof error === "string" || error instanceof String) &&
                    error.toLowerCase().includes("not authenticated"))
            ) {
                await showAlert({
                    title: "Logged out",
                    text: "You were logged out for security reasons. Please login again.",
                    icon: "error",
                    buttons: true
                });

                return await UserActions.signOut();
            }

            if (code === 426) {
                const profile = error?.response?.data?.profile;
                const message = error?.response?.data?.message;
                if (profile) {
                    //if you are localhost then set your "simulatedProfile"
                    //to remember that a redirect has indeed occurred
                    //otherwise do what the server tells you to and do a switch
                    console.info(
                        `Upgrade detected moving to ${profile}`,
                        message
                    );
                    if (window.location.hostname === "localhost") {
                        setCookie(
                            "simulatedLocalRedirectProfile",
                            profile,
                            "/",
                            30
                        );
                        window.location = `/${profile}/index.html`;
                    } else {
                        window.location = `/${profile}/index.html`;
                    }

                    return;
                } else {
                    //what do you do if no profile was sent back?? logout? catastrophe?
                }
            }

            if (code === 503) {
                const subCode = error?.response?.data?.subCode;
                if (subCode === "NO_PERM") {
                    throw {
                        title: "Temporary Outage",
                        errorMessage:
                            "We are currently performing some maintenance on our servers. Please try again in a few moments"
                    };
                }
            }
            //what to do in read only mode

            const data =
                (error && error.response && error.response.data) || error;
            const text =
                (data && data.errorMessage) ||
                (data && data.toString()) ||
                "Something went wrong";
            const title = data?.title || "Something went wrong";

            throw {
                title,
                text,
                ...data
            };
        }

        throw error;
    } finally {
        if (abortSignal && abortListener) {
            abortSignal.removeEventListener("abort", abortListener);
        }
    }
}

function Utf8ArrayToStr(array) {
    var out, i, len, c;
    var char2, char3;

    out = "";
    len = array.length;
    i = 0;
    while (i < len) {
        c = array[i++];
        switch (c >> 4) {
            case 0:
            case 1:
            case 2:
            case 3:
            case 4:
            case 5:
            case 6:
            case 7:
                // 0xxxxxxx
                out += String.fromCharCode(c);
                break;
            case 12:
            case 13:
                // 110x xxxx   10xx xxxx
                char2 = array[i++];
                out += String.fromCharCode(((c & 0x1f) << 6) | (char2 & 0x3f));
                break;
            case 14:
                // 1110 xxxx  10xx xxxx  10xx xxxx
                char2 = array[i++];
                char3 = array[i++];
                out += String.fromCharCode(
                    ((c & 0x0f) << 12) |
                        ((char2 & 0x3f) << 6) |
                        ((char3 & 0x3f) << 0)
                );
                break;
        }
    }

    return out;
}
